import { createContext, useContext, type ReactNode } from 'react';
import { useGetSignupConfigQuery } from '../services/nestjs-base-api';

// Charter Cohort (SYR-106; semantics changed by SYR-109).
//
// While the intake is open, /charter serves the application ALONGSIDE normal
// signup — the flag no longer takes /sign-up over. Read at app bootstrap,
// exactly like the dashboard config, so the route tree is stable before
// anything mounts.
//
// This shares useGetSignupConfigQuery with SignupPage, so RTK Query serves both
// from one request.

interface CharterConfig {
    // The Charter intake is open: /charter is live, alongside normal signup.
    charterCohortEnabled: boolean;
    // Config resolved (public endpoint returned or failed).
    ready: boolean;
}

const CharterConfigContext = createContext<CharterConfig>({
    charterCohortEnabled: false,
    ready: false,
});

export const CharterConfigProvider = ({ children }: { children: ReactNode }) => {
    const { data, isError } = useGetSignupConfigQuery();

    // A failed config fetch means normal signup, which is the state the platform
    // returns to after the intake anyway.
    const ready = data !== undefined || isError;
    const value: CharterConfig = {
        charterCohortEnabled: data?.charterCohortEnabled ?? false,
        ready,
    };

    return (
        <CharterConfigContext.Provider value={value}>
            {children}
        </CharterConfigContext.Provider>
    );
};

export const useCharterConfig = (): CharterConfig => useContext(CharterConfigContext);
